import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "@/hooks/useTheme";
import { MessageCircle, MoreVertical, Sun, Moon, Monitor, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

export type HeaderProps = {
  /** Landing principal: sem troca de tema; combina com tema claro forçado na página. */
  hideThemeSelector?: boolean;
};

export const Header = ({ hideThemeSelector = false }: HeaderProps) => {
  const { theme, setTheme } = useTheme();

  return (
    <header className="absolute top-4 md:top-6 z-50 w-full px-4 flex justify-center">
      <div className={cn(
        "container mx-auto max-w-5xl rounded-full border shadow-md backdrop-blur",
        hideThemeSelector 
          ? "bg-white/5 border-white/15 supports-[backdrop-filter]:bg-white/10 text-white" 
          : "bg-background/95 border-border/40 supports-[backdrop-filter]:bg-background/60"
      )}>
        <div className="flex h-14 w-full items-center justify-between gap-2 px-4 md:h-16 md:gap-4 md:px-8">
          <Link to="/" className="flex min-w-0 shrink-0 items-center text-xl font-bold">
            {hideThemeSelector ? (
              <img
                src="/logo.png?v=4"
                alt="FabChat Logo"
                width={160}
                height={40}
                fetchPriority="high"
                className="h-6 sm:h-7 w-auto flex-shrink-0 object-contain border-none"
              />
            ) : (
              <>
                <img
                  src="/logo-light.png?v=4"
                  alt="FabChat Logo"
                  width={160}
                  height={40}
                  fetchPriority="high"
                  className="h-6 sm:h-7 w-auto flex-shrink-0 object-contain border-none block dark:hidden"
                />
                <img
                  src="/logo.png?v=4"
                  alt="FabChat Logo"
                  width={160}
                  height={40}
                  fetchPriority="high"
                  className="h-6 sm:h-7 w-auto flex-shrink-0 object-contain border-none hidden dark:block"
                />
              </>
            )}
          </Link>

          <nav className="hidden min-w-0 md:flex md:flex-1 md:items-center md:justify-center md:gap-8">
            <Link
              to="/#features"
              className={cn("text-sm font-medium transition-colors", hideThemeSelector ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-primary")}
            >
              Recursos
            </Link>
            <Link
              to="/#pricing"
              className={cn("text-sm font-medium transition-colors", hideThemeSelector ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-primary")}
            >
              Preços
            </Link>
            <Link 
              to="/#faq" 
              className={cn("text-sm font-medium transition-colors", hideThemeSelector ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-primary")}
            >
              FAQ
            </Link>
            <Link
              to="/sobre"
              className={cn("text-sm font-medium transition-colors", hideThemeSelector ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-primary")}
            >
              Sobre
            </Link>
            <Link
              to="/contato"
              className={cn("text-sm font-medium transition-colors", hideThemeSelector ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-primary")}
            >
              Contato
            </Link>
          </nav>

          <div className="flex shrink-0 items-center gap-1 sm:gap-2 md:gap-4 md:justify-end">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "md:hidden h-8 shrink-0 rounded-full px-3 text-xs sm:h-9 sm:px-4 sm:text-sm",
                hideThemeSelector
                  ? "text-white/90 hover:bg-white/10 hover:text-white"
                  : "text-foreground hover:bg-muted/60 hover:text-foreground",
              )}
              asChild
            >
              <Link to="/auth">Entrar</Link>
            </Button>
            <div className="md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-9 w-9 shrink-0",
                      hideThemeSelector && "text-white/90 hover:bg-white/10 hover:text-white",
                    )}
                    aria-label="Menu"
                  >
                    <MoreVertical className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {!hideThemeSelector && (
                    <>
                      <DropdownMenuItem onClick={() => setTheme("light")}>
                        <Sun className="mr-2 h-4 w-4" />
                        Claro
                        {theme === "light" && <span className="ml-auto">✓</span>}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setTheme("dark")}>
                        <Moon className="mr-2 h-4 w-4" />
                        Escuro
                        {theme === "dark" && <span className="ml-auto">✓</span>}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setTheme("system")}>
                        <Monitor className="mr-2 h-4 w-4" />
                        Sistema
                        {theme === "system" && <span className="ml-auto">✓</span>}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to="/sobre" className="flex items-center cursor-pointer">
                      Sobre
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/contato" className="flex items-center cursor-pointer">
                      <MessageCircle className="mr-2 h-4 w-4" />
                      Contato
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/auth" className="flex items-center cursor-pointer font-medium text-primary">
                      <Sparkles className="mr-2 h-4 w-4" />
                      Começar agora
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="hidden md:flex items-center gap-2">
              {!hideThemeSelector && <ThemeToggle />}
              <Button 
                variant="ghost" 
                size="sm" 
                className={cn("h-9 px-4 rounded-full", hideThemeSelector && "text-white/90 hover:text-white hover:bg-white/10")} 
                asChild
              >
                <Link to="/auth">Entrar</Link>
              </Button>
              <Button size="sm" className="h-9 px-6 rounded-full" asChild>
                <Link to="/auth">Começar agora</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
